// eslint-disable-next-line import/no-named-as-default
import { useProjectContext } from "components/project/projects/context";
import { useSelectedProjectPermissions } from "components/project/projects/hooks";
import { useCreateProjectShapefileFolder } from "components/project/shapefiles/hooks/createProjectShapefileFolder/useCreateProjectShapefileFolder";
import {
  useDeleteProjectShapefileFolderMutation,
  useSortProjectShapefilesInFolderMutation
} from "components/project/shapefiles/mutations";

import { useUser } from "../../../../../../../hooks";
import { DeletableFolderNode } from "../../../../utils/collectCheckedShapefileIds";
import { UseRightClickContextMenuReturn } from "../hooks/useRightClickContextMenu";
import { ShapefileFolderActions } from "./ShapefileFolderActions";

interface IProjectShapefileFolderActionsProps {
  newFolderPosition: number;
  shapefileNodeId: string;
  setIsRenaming: () => void;
  contextMenu?: UseRightClickContextMenuReturn;
  hasSelectItemsAction?: boolean;
  folderName: string;
  // The folder node with its children, so the delete can name the shapefiles
  // the cascade takes with it (EVA-126).
  folderNode?: DeletableFolderNode;
}

/**
 * Some actions and properties have been left for dev to implement based on use case, since the properties are different between
 * the layer tree layers, and layer list layers.
 * @param params
 * @returns
 */
export function ProjectShapefileFolderActions(
  params: IProjectShapefileFolderActionsProps
): JSX.Element {
  const { selectedProjectId } = useProjectContext();
  const { hasTrialAccount, isReadonly } = useUser();
  const projectPermissions = useSelectedProjectPermissions();

  const deleteProjectShapefileFolderMutation = useDeleteProjectShapefileFolderMutation({
    projectId: selectedProjectId
  });

  const sortProjectShapefilesInFolderMutation = useSortProjectShapefilesInFolderMutation({
    projectId: selectedProjectId
  });

  const createProjectFolder = useCreateProjectShapefileFolder({
    projectId: selectedProjectId
  });

  return (
    <ShapefileFolderActions
      addFolder={() => {
        createProjectFolder.mutate({
          parentId: params.shapefileNodeId,
          order: params.newFolderPosition
        });
      }}
      deleteFolder={() =>
        deleteProjectShapefileFolderMutation.mutate({
          shapefileNodeId: params.shapefileNodeId,
          folderNode: params.folderNode
        })
      }
      canExport={!hasTrialAccount && !isReadonly}
      hasColorPicker={true}
      hasSelectItems={params.hasSelectItemsAction ?? true}
      isDisabled={!projectPermissions.canEditShapefiles}
      sortShapefilesAscend={() =>
        sortProjectShapefilesInFolderMutation.mutate({
          shapefileNodeId: params.shapefileNodeId,
          order: "asc"
        })
      }
      sortShapefilesDescend={() =>
        sortProjectShapefilesInFolderMutation.mutate({
          shapefileNodeId: params.shapefileNodeId,
          order: "desc"
        })
      }
      folderName={params.folderName}
      contextMenu={params.contextMenu}
      {...params}
    />
  );
}
